import { Link } from 'react-router-dom';
import { Property } from '@/hooks/useProperties';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Bed, Bath, Maximize, MapPin, Eye } from 'lucide-react';

interface PropertyCardProps {
  property: Property;
}

export const PropertyCard = ({ property }: PropertyCardProps) => {
  const getTransactionLabel = () => {
    if (property.transaction_type === 'both') return 'Sale & Rent';
    if (property.transaction_type === 'sale') return 'For Sale';
    return 'For Rent';
  };

  const getStatusColor = () => {
    switch (property.status) {
      case 'available': return 'bg-green-500';
      case 'reserved': return 'bg-yellow-500';
      case 'sold': return 'bg-red-500';
      case 'rented': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const formatPrice = (price: number | null) => {
    if (!price) return 'Contact for price';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: property.currency || 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Link to={`/real-estate/${property.slug}`}>
      <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 animate-fade-in">
        {/* Image */}
        <div className="relative h-64 overflow-hidden">
          <img
            src={property.main_image_url || '/placeholder.svg'}
            alt={property.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          
          {/* Badges */}
          <div className="absolute top-4 left-4 flex flex-col gap-2">
            <Badge className="bg-primary/90 text-white">
              {getTransactionLabel()}
            </Badge>
            {property.is_featured && (
              <Badge className="bg-accent/90 text-white">
                Featured
              </Badge>
            )}
            <Badge className={`${getStatusColor()} text-white`}>
              {property.status.charAt(0).toUpperCase() + property.status.slice(1)}
            </Badge>
          </div>

          {/* Views */}
          <div className="absolute top-4 right-4 bg-black/50 text-white px-2 py-1 rounded-full flex items-center gap-1 text-xs">
            <Eye className="h-3 w-3" />
            {property.views_count}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Title */}
          <h3 className="text-xl font-bold mb-2 text-foreground group-hover:text-primary transition-colors line-clamp-1">
            {property.title}
          </h3>

          {/* Location */}
          <div className="flex items-center text-muted-foreground mb-4">
            <MapPin className="h-4 w-4 mr-1" />
            <span className="text-sm line-clamp-1">
              {property.location_zone && `${property.location_zone}, `}
              {property.location_city}
              {property.location_province && `, ${property.location_province}`}
            </span>
          </div>

          {/* Price */}
          <div className="mb-4">
            <p className="text-2xl font-bold text-primary">
              {formatPrice(property.transaction_type === 'rent' ? property.rental_price : property.price)}
              {property.transaction_type === 'rent' && (
                <span className="text-sm text-muted-foreground">/month</span>
              )}
            </p>
            {property.transaction_type === 'both' && property.rental_price && (
              <p className="text-sm text-muted-foreground">
                Rent: {formatPrice(property.rental_price)}/month
              </p>
            )}
          </div>

          {/* Specs */}
          <div className="flex items-center gap-4 pt-4 border-t border-border">
            {property.bedrooms !== null && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Bed className="h-4 w-4" />
                <span className="text-sm">{property.bedrooms}</span>
              </div>
            )}
            {property.bathrooms !== null && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Bath className="h-4 w-4" />
                <span className="text-sm">{property.bathrooms}</span>
              </div>
            )}
            {property.area_sqm !== null && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Maximize className="h-4 w-4" />
                <span className="text-sm">{property.area_sqm} m²</span>
              </div>
            )}
          </div>
        </div>
      </Card>
    </Link>
  );
};
